import { Box, BoxProps, Button, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useResource } from 'datx-jsonapi-react';
import NextLink from 'next/link';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import ContactUs from '@/assets/icons/ic-nav-help-desk.svg';
import TicketIcon from '@/assets/icons/ic-ticket.svg';

import { useQuery } from '@tanstack/react-query';

import { EventTicketDescriptionMarkdown } from '@/components/ui/shared/markdowns/EventTicketDescriptionMarkdown/EventTicketDescriptionMarkdown';
import { queryThinEvent } from '@/queries/thin-events';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';

interface IExternalAAPTProps extends BoxProps {
  eventId: string;
}

export const ExternalAAPT: FC<IExternalAAPTProps> = ({ eventId, ...props }) => {
  const { t } = useTranslation();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;

  const { data: event } = useResource(queryThinEvent(eventId));

  return (
    <Box shadow="card" bgColor="white" borderRadius={2} p={4} {...props}>
      <Box borderRadius={2} bgColor="gray.50" p={4} mb={{ base: 4, lg: 8 }}>
        <Flex justifyContent="flex-start" alignItems="center" mb={2}>
          <Icon w={6} h={6} as={TicketIcon} mr={2} color="black" />
          <Text as="h4" fontSize="1.125rem" lineHeight="short">
            {t('eventTicketRegister.registrationFeeTitle')}
          </Text>
        </Flex>
        {event.missingTicketDescription ? (
          <EventTicketDescriptionMarkdown source={event.missingTicketDescription} />
        ) : (
          <Text>{t('eventTicketRegister.registrationFeeMessage')}</Text>
        )}
      </Box>
      <Flex mb={2} flexDirection={{ base: 'column', lg: 'row' }} alignItems="center">
        <Flex
          justifyContent="space-between"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={2}
          alignItems={{ base: 'flex-start', md: 'center' }}
          w="full"
        >
          {!user && (
            <Flex justifyContent="flex-start" alignItems="center">
              <Text as="span" mr={4}>
                {t('eventTicketRegister.alreadyRegistered')}
              </Text>
              <NextLink href={event.externalLoginUrl} passHref legacyBehavior>
                <Button size="sm">{t('eventTicketRegister.logIn')}</Button>
              </NextLink>
            </Flex>
          )}
          <Box>
            <Text as="span" mr={4}>
              {t('eventTicketRegister.needHelp')}
            </Text>
            <NextLink
              href={{
                pathname: '/contact-us/support',
                query: { eventId: event.id },
              }}
              passHref
              legacyBehavior
            >
              <Link fontWeight="bold">
                {t('eventTicketRegister.contactUs')} <Icon as={ContactUs} ml={2} />
              </Link>
            </NextLink>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};
